import { PRICING_PAGE_TITLE } from './consts';
import { environment } from '../../../environments/environment';

// Fallback og:image/twitter:image for every page that has no dedicated
// social-share image of its own.
export const DEFAULT_OG_IMAGE = `${environment.domainUrl}assets/icons/logo-horizontal-og-image.png`;

export interface SeoTagsInfo {
    title: string;
    canonicalSlug?: string;
    tags: {
        name?: string;
        property?: string;
        content: string;
    }[];
}

export const mapPageTags: SeoTagsInfo = {
    title: 'Map AI | Location analysis tool',
    tags: [
      {
        name: 'description',
        content:
          'Find where to move and do business with a 1-mile resolution at Map AI location intelligence platform. Access demand forecasting, competitive market analysis, and neighborhood analysis, including detailed demographics by zip code vs. 1 mile, real estate analytics, and more.',
      },
      {
        name: 'keywords',
        content:
          'Map-ai, population density by zip, median income by zip code map, median income map, median household income map, retail store location analysis, location analysis, location intelligence, competitive landscape analysis, map ai',
      },
      {
        property: 'og:title',
        content: 'Map AI | Location analysis tool',
      },
      {
        property: 'og:description',
        content:
          'Find where to move and do business with a 1-mile resolution at Map AI location intelligence platform.',
      },
      {
        property: 'og:url',
        content: 'https://interactive-map-ai.com/',
      },
      {
        property: 'og:image',
        content: DEFAULT_OG_IMAGE,
      },
    ]
};

export const aboutAiChatPageTags: SeoTagsInfo = {
  title: 'How AI Business Location Analysis Works | Map AI',
  canonicalSlug: '/chat/about',
  tags: [
    {
      name: 'description',
      content:
        'Learn how Map AI analyzes any US business location. Ask about demographics, competition, and traffic — get real data and map visualizations. No analyst needed.',
    },
    {
      name: 'keywords',
      content:
        'how business location analysis works, location intelligence tool guide, demographic data for small business, site selection research, ai location comparison, map ai help',
    },
    {
      property: 'og:title',
      content: 'How AI Business Location Analysis Works | Map AI',
    },
    {
      property: 'og:description',
      content:
        'Learn how Map AI analyzes any US business location. Ask about demographics, competition, and traffic — get real data and map visualizations. No analyst needed.',
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/chat/about',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}

export const aiPageTags: SeoTagsInfo = {
  title: 'Ask AI Map About Any US Neighborhood | Map AI',
  canonicalSlug: '/chat',
  tags: [
    {
      name: 'description',
      content:
        'Ask about any US address and AI connects to the relevant location intelligence data: demographics, income, housing, traffic and nearby places, on a live map.',
    },
    {
      name: 'keywords',
      content:
        'ask ai about neighborhood, ai map, neighborhood data by address, location intelligence, demographic data, income data, housing data, traffic data, business location analysis, map ai',
    },
    {
      property: 'og:title',
      content: 'Ask AI Map About Any US Neighborhood | Map AI',
    },
    {
      property: 'og:description',
      content:
        'Ask about any US address and AI connects to the relevant location intelligence data: demographics, income, housing, traffic and nearby places, on a live map.',
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/chat',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}
export const reportsPageTags: SeoTagsInfo = {
  title: 'Demographic & Location PDF Reports Online | Map AI',
  canonicalSlug: '/reports/order',
  tags: [
    {
      name: 'description',
      content:
        'Order a PDF report for any US address: demographics within a radius or drive time, neighborhood profiles, traffic counts, and feasibility studies.',
    },
    {
      name: 'keywords',
      content:
        'demographic report, demographic pdf report, radius demographics report, drive time demographics report, neighborhood report, location analysis report, traffic count report, site feasibility study',
    },
    {
      property: 'og:title',
      content: 'Demographic & Location PDF Reports Online | Map AI',
    },
    {
      property: 'og:description',
      content:
        'PDF reports for any US address: demographics within a radius or drive time, neighborhood profiles, traffic, and feasibility studies.',
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/reports/order',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}

export const pricingPageTags: SeoTagsInfo = {
  title: PRICING_PAGE_TITLE,
  canonicalSlug: '/pricing',
  tags: [
    {
      name: 'description',
      content:
        'Map AI pricing: subscribe for ongoing neighborhood and site-selection analysis, or buy a single PDF report for one address. No hidden fees.',
    },
    {
      name: 'keywords',
      content:
        'map ai pricing, location intelligence pricing, neighborhood report price, site selection subscription cost, real estate data subscription',
    },
    {
      property: 'og:title',
      content: PRICING_PAGE_TITLE,
    },
    {
      property: 'og:description',
      content:
        'Subscribe for ongoing analysis, or buy a single PDF report for one address. Clear pricing, no hidden fees.',
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/pricing',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}

const DATA_SOURCES_TITLE = 'Data Sources: Census, CDC, FBI and More | Map AI';
const DATA_SOURCES_DESCRIPTION =
  'The public datasets behind Map AI: U.S. Census demographics, CDC health data, FBI crime reports, school records, traffic counts and OpenStreetMap places.';

export const dataSourcesPageTags: SeoTagsInfo = {
  title: DATA_SOURCES_TITLE,
  canonicalSlug: '/info/data-sources',
  tags: [
    {
      name: 'description',
      content: DATA_SOURCES_DESCRIPTION,
    },
    {
      name: 'keywords',
      content:
        'map ai data sources, american community survey data, cdc places health data, fbi nibrs crime data, nces school data, traffic count data, location intelligence data',
    },
    {
      property: 'og:title',
      content: DATA_SOURCES_TITLE,
    },
    {
      property: 'og:description',
      content: DATA_SOURCES_DESCRIPTION,
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/info/data-sources',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}

const HWW_TITLE = 'Healthy, Wealthy and Wise Index Methodology | Map AI';
const HWW_DESCRIPTION =
  'How Map AI’s Healthy, Wealthy and Wise index rates US locations on health, wealth and education: life expectancy, clinical care, income, housing and schools.';

export const hwwPageTags: SeoTagsInfo = {
  title: HWW_TITLE,
  canonicalSlug: '/info/healthy-wealthy-and-wise',
  tags: [
    {
      name: 'description',
      content: HWW_DESCRIPTION,
    },
    {
      name: 'keywords',
      content:
        'healthy wealthy and wise index, health index by location, wealth index by location, education index, quality of life index us, neighborhood well-being score',
    },
    {
      property: 'og:title',
      content: HWW_TITLE,
    },
    {
      property: 'og:description',
      content: HWW_DESCRIPTION,
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/info/healthy-wealthy-and-wise',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}

// Title and description are written against the page's own h1 ("Compare
// Two Addresses Side by Side") - a result whose title, heading and opening
// paragraph answer the same query is the whole point of the pairing. Radius
// and drive-time wording is left to the trade-area pages, which own those
// queries; this page owns "compare locations".
export const COMPARE_LOCATIONS_TITLE =
  'Compare Locations: Two Addresses Side by Side | Map AI';

const COMPARE_LOCATIONS_DESCRIPTION =
  'Compare two or more US addresses side by side: population, income, ' +
  'health, housing and spending within a radius or drive time you pick. ' +
  'Free, no account needed.';

export const compareLocationsPageTags: SeoTagsInfo = {
  title: COMPARE_LOCATIONS_TITLE,
  canonicalSlug: '/comparative-location-tool',
  tags: [
    {
      name: 'description',
      content: COMPARE_LOCATIONS_DESCRIPTION,
    },
    {
      name: 'keywords',
      content:
        'compare locations, compare two addresses, compare neighborhoods, compare demographics by address, location comparison tool, side by side location comparison, site comparison, Map ai',
    },
    {
      property: 'og:title',
      content: COMPARE_LOCATIONS_TITLE,
    },
    {
      property: 'og:description',
      content: COMPARE_LOCATIONS_DESCRIPTION,
    },
    {
      property: 'og:type',
      content: 'website',
    },
    {
      property: 'og:url',
      content: `${environment.domainUrl}comparative-location-tool`,
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
    {
      name: 'twitter:title',
      content: COMPARE_LOCATIONS_TITLE,
    },
    {
      name: 'twitter:description',
      content: COMPARE_LOCATIONS_DESCRIPTION,
    },
  ],
};
export const settingsPageTags: SeoTagsInfo = {
  title: 'Map AI | Location analysis',
  canonicalSlug: '/user/subscription',
  tags: [
    {
      name: 'description',
      content:
        'Accessible location analysis for life and business: 50+ parameters at 1-mile resolution, starting from $4.95.',
    },
    {
      name: 'keywords',
      content:
        'Map ai subscription, map-ai, mapai, best location intelligence software, coffee shop location analysis, grocery demand forecasting, retail store location analysis, laundromat location analysis, median household income by zip code map, map of median income',
    },
    {
      property: 'og:title',
      content: 'Map AI | Location analysis',
    },
    {
      property: 'og:description',
      content:
        'Accessible location analysis for life and business: 50+ parameters at 1-mile resolution.',
    },
    {
      property: 'og:url',
      content: 'https://interactive-map-ai.com/user/subscription',
    },
    {
      property: 'og:image',
      content: DEFAULT_OG_IMAGE,
    },
  ]
}
